// src/components/LowRound/RoundDetail.js
import React, { useState, Suspense } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  Avatar,
  Box,
} from "@mui/material";
import FullScorecardTable from "../Scorecard/FullScorecardTable";
import { formatInTimeZone } from "date-fns-tz";

// Lazy-load ProfileView (if needed elsewhere), though now ProfileView will only be used if showProfileInfo is true.
// (We keep lazy loading here to break any cyclic dependency, if necessary.)
const ProfileViewLazy = React.lazy(() => import("../Profile/ProfileView"));

// Helper: Convert a Firestore timestamp or ISO string to a Date.
const toDate = (value) => {
  if (!value) return null;
  if (typeof value === "object" && value.seconds) return new Date(value.seconds * 1000);
  return new Date(value);
};

// Format a Date in Eastern Time.
const formatDateInEastern = (date) =>
  formatInTimeZone(date, "America/New_York", "EEE, MMM d, yyyy");

// Helper: Given a placement index (0-based), return a placement label and its color.
const getPlacementLabelAndColor = (index) => {
  const rank = index + 1;
  if (rank === 1) return { label: "1st Place", color: "#FFD700" };
  if (rank === 2) return { label: "2nd Place", color: "#C0C0C0" };
  if (rank === 3) return { label: "3rd Place", color: "#cd7f32" };
  return { label: `${rank}th Place`, color: "inherit" };
};

// Helper: Compute player's initials from a name.
const getInitials = (name) => {
  if (!name) return "";
  const parts = name.split(" ");
  return parts.map((p) => p[0]).join("");
};

const RoundDetail = ({
  round,
  competitionWeek,
  placementIndex,
  playerName,
  playerBagTag,
  playerPhotoURL,
  onClose,
  isCompetition, 
  showProfileInfo = true, // New prop: defaults to true
}) => {
  const [openProfile, setOpenProfile] = useState(false);

  if (!round || !competitionWeek || placementIndex === null) {
    return (
      <Container sx={{ mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  // Compute aggregate scores.
  let totalScore = 0;
  let totalPar = 0;
  (round.scores || []).forEach((s) => {
    totalScore += s.score || 0;
    totalPar += s.par || 0;
  });
  const scoreDiff = totalScore - totalPar;
  const roundDateObj = round.roundDate ? toDate(round.roundDate) : null;

  // Get placement label and color.
  const placementObj = getPlacementLabelAndColor(placementIndex);
  const scoresArray = (round.scores || []).map((s) => s.score);

  // Build tee data robustly.
  const selectedTeeObj =
    round.teeData && round.selectedTee
      ? {
          tee_name: round.selectedTee,
          holes:
            round.teeData[round.selectedTee] &&
            Array.isArray(round.teeData[round.selectedTee].yardages)
              ? round.teeData[round.selectedTee].yardages.map((yardage, index) => ({
                  yardage,
                  par:
                    round.scores && round.scores[index]
                      ? parseInt(round.scores[index].par, 10) || 0
                      : 0,
                  handicap:
                    round.scores && round.scores[index]
                      ? round.scores[index].handicap || ""
                      : "",
                  hole_number: index + 1,
                }))
              : [],
        }
      : { holes: [] };

  return (
    <Container sx={{ mt: 4 }}>
      {showProfileInfo ? (
        // SHOW USER PROFILE INFO
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Avatar
            src={playerPhotoURL || ""}
            onClick={() => setOpenProfile(true)}
            sx={{
              width: 80,
              height: 80,
              mr: 2,
              cursor: "pointer",
            }}
          >
            {getInitials(playerName)}
          </Avatar>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            {playerName} ({playerBagTag})
            {isCompetition && (
              <>
                {" - "}
                <span style={{ color: placementObj.color }}>
                  {placementObj.label}
                </span>
                <br />
                Week of {formatDateInEastern(competitionWeek)}
              </>
            )}
          </Typography>
        </Box>
      ) : (
        // SHOW ONLY COURSE PLAYED AS HEADER
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: "bold" }}>
            {round.clubName || "Unknown Club"}
          </Typography>
        </Box>
      )}

      {/* ProfileView popup wrapped in Suspense */}
      {showProfileInfo && (
        <Suspense fallback={<CircularProgress size={24} />}>
          <ProfileViewLazy
            open={openProfile}
            onClose={() => setOpenProfile(false)}
            userId={round.userId}
          />
        </Suspense>
      )}

      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        {round.clubName || "Unknown Club"} –{" "}
        {roundDateObj ? formatDateInEastern(roundDateObj) : "No Date"}
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        Course Rating: {round.course_rating || "N/A"}
      </Typography>
      <Typography variant="body2">
        Slope Rating: {round.slope_rating || "N/A"}
      </Typography>
      <Typography variant="body2" sx={{ mt: 2 }}>
        Total Score: {totalScore} ({scoreDiff >= 0 ? `+${scoreDiff}` : scoreDiff})
      </Typography>
      {round.differential !== undefined && (
        <Typography variant="body2">
          Differential:{" "}
          {round.differential >= 0
            ? `+${round.differential.toFixed(1)}`
            : round.differential.toFixed(1)}
        </Typography>
      )}
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        Full Scorecard
      </Typography>
      <FullScorecardTable
        readOnly={true}
        scores={scoresArray}
        selectedTee={selectedTeeObj}
      />
    </Container>
  );
};

export default RoundDetail;